import React, { useContext, useEffect, useMemo, useState } from 'react';

// DEPENDENCIES
import clsx from 'clsx';

// GLOBAL VARIABLES
import { NUMBER_FORMATS } from 'numberFormats';

// GLOBAL FUNCTIONS
import { modalFunctions } from 'functions';

// CORE COMPONENTS
import Form from 'core/form/Form';
import Button from 'core/tools/Button';

// LOCAL CONTEXT
import { AdjustedPriceContext } from './PriceAdjustmentWrapper';

// STYLES
import styles from './priceAdjustment.module.scss';

// VARIABLES
const ADJUSTMENT_OPTIONS = [
  {
    label: 'By Selling Price',
    value: 0
  },
  {
    label: 'By Selling Price in %',
    value: 1
  },
  {
    label: 'By IMV Price',
    value: 2
  },
  {
    label: 'By IMV Price in %',
    value: 3
  }
]

// MAIN COMPONENT
const PriceAdjustment = ({
  row: {
    original: {
      advertised_price,
      imv_price,
    } = {}
  } = {},
}) => {
  // CONTEXT
  const { setAdjustedPrice } = useContext(AdjustedPriceContext)

  // STATE
  const [ adjustmentOption, setAdjustmentOption ] = useState(0);
  const [ adjustmentMultiplier, setAdjustmentMultiplier ] = useState(0);
  const [ adjustmentValue, setAdjustmentValue ] = useState();

 
  // MEMOS
  const adjustmentOptions = useMemo(() => {
    if (imv_price) {
      if (advertised_price) {
        setAdjustmentOption(0);
        return ADJUSTMENT_OPTIONS;
      }
      setAdjustmentOption(2);
      return ADJUSTMENT_OPTIONS.slice(2);
    } else {
      setAdjustmentOption(0);
      return ADJUSTMENT_OPTIONS.slice(0, 2);
    }
  }, [imv_price, advertised_price]);

  const isPercent = useMemo(
    () => adjustmentOption%2,
    [adjustmentOption]
  )

  // EFFECTS
  useEffect(
    () => {
      if (adjustmentMultiplier === undefined || adjustmentValue === undefined) setAdjustedPrice(undefined);
      let adjustmentAmount = parseFloat(adjustmentValue) * adjustmentMultiplier;
      if (isPercent) {
        if (Math.abs(adjustmentAmount) > 20) {
          modalFunctions.error('The adjustment must be a whole number in the range -20 to 20.')
          setAdjustedPrice(undefined);
          return;
        }
        adjustmentAmount *= .01;
        adjustmentAmount += 1;
      };
      switch(adjustmentOption) {
        case 0:
          setAdjustedPrice(parseFloat(advertised_price) + adjustmentAmount);
          return;
        case 1:
          setAdjustedPrice(parseFloat(advertised_price) * adjustmentAmount);
          return;
        case 2:
          setAdjustedPrice(parseFloat(imv_price) + adjustmentAmount);
          return;
        case 3:
          setAdjustedPrice(parseFloat(imv_price) * adjustmentAmount);
          return;
        default:
          return;
      }
    },
    [adjustmentOption, adjustmentMultiplier, adjustmentValue, isPercent, advertised_price, imv_price, setAdjustedPrice]
  )

  const disabled = useMemo(() => {
    if (advertised_price || parseInt(advertised_price) === 0 ) return false;
    else if (imv_price) return false;
    return true;
  }, [advertised_price, imv_price]);

  // RENDER
  return (
    !disabled && (
      <Form.Body.Inline>
        <Form.Select
          className={styles.select}
          value={adjustmentOption}
          options={adjustmentOptions}
          onSelect={value => {
            setAdjustmentOption(value);
            setAdjustmentValue(undefined);
          }}
          useFormik={false}
          htmlSize={12}
          size="sm"
          cols={{
            xs: 24,
            lg: 14
          }}
          menuPlacement="bottom"
          disabled={disabled}
        />
        <Form.Control
          className={clsx(
            'mt-n3 mt-lg-0',
            isPercent && adjustmentValue > 20 && styles.error
          )}
          value={adjustmentValue}
          onBlur={e => setAdjustmentValue(e.target.value.replace(/[^0-9.-]+/g,""))}
          numberFormat={isPercent ? NUMBER_FORMATS.percent : {
            ...NUMBER_FORMATS.dollars,
            decimalScale: 0
          }}
          prepend={{
            className: clsx(
              isPercent && adjustmentValue > 20 && styles.error
            ),
            children: disabled ? undefined : (
              <div className="d-flex">
                <ToggleButton
                  multiplier={adjustmentMultiplier}
                  onClick={setAdjustmentMultiplier}
                  defaultValue={0}
                  value={-1}
                  label="-"
                />
                <ToggleButton
                  multiplier={adjustmentMultiplier}
                  onClick={setAdjustmentMultiplier}
                  value={1}
                  label="+"
                />
              </div>
            )
          }}
          useFormik={false}
          size="sm"
          cols={{
            xs: 24,
            lg: 10
          }}
          disabled={disabled}
        />
      </Form.Body.Inline>
    )
  );
};

// EXPORT
export default PriceAdjustment

function ToggleButton ({
  multiplier,
  onClick,
  defaultValue,
  value,
  label
}) {
  const highlighted = useMemo(() => [defaultValue, value].includes(multiplier), [defaultValue, multiplier, value]);
  return (
    <Button
      className={styles.toggle}
      variant={highlighted ? 'primary' : 'light'}
      outline={!highlighted}
      label={label}
      onClick={() => onClick(value)}
    />
  );
}